import { Link } from "react-router-dom";
import { useContent } from "@/context/ContentContext";
import { SERVICES, ASSETS } from "@/lib/services";

export default function Home() {
  const { t } = useContent();
  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative bg-ink-800 text-bone-50 overflow-hidden">
        <div
          className="absolute inset-0 opacity-30"
          style={{
            backgroundImage: `url(${ASSETS.athensHero})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-ink-800 via-ink-800/85 to-ink-800/40" />

        <div className="relative section pt-24 pb-24 md:pt-32 md:pb-40 grid md:grid-cols-12 gap-12 items-center">
          <div className="md:col-span-7 animate-fade-up">
            <div className="eyebrow !text-gold-400 mb-6" data-testid="hero-eyebrow">
              {t("hero.eyebrow")}
            </div>
            <div className="font-serif italic text-gold-400 text-lg md:text-xl mb-6" data-testid="hero-attorney-name">
              Vasileios Filippidis · <span className="not-italic uppercase tracking-[0.22em] text-xs text-gold-400/80">Attorney-at-Law</span>
            </div>
            <h1
              className="font-serif font-light text-5xl sm:text-6xl lg:text-7xl leading-[1.05] tracking-tight max-w-3xl"
              data-testid="hero-title"
            >
              {t("hero.title")}
            </h1>
            <p className="mt-8 text-lg md:text-xl text-bone-100/80 max-w-2xl leading-relaxed font-light">
              {t("hero.subtitle")}
            </p>
            <div className="mt-6 flex flex-wrap items-center gap-x-3 gap-y-2 text-sm md:text-base text-gold-400 tracking-wide" data-testid="hero-service-links">
              {[
                { label: "Real Estate", slug: "real-estate" },
                { label: "Golden Visa", slug: "golden-visa" },
                { label: "Company Formation", slug: "company-formation" },
                { label: "AFM Registration", slug: "afm-registration" },
                { label: "Banking Services", slug: "banking" },
              ].map((s, i, arr) => (
                <span key={s.slug} className="flex items-center gap-x-3">
                  <Link
                    to={`/services/${s.slug}`}
                    className="hover:text-bone-50 transition-colors underline-offset-4 hover:underline decoration-gold-500/60"
                    data-testid={`hero-service-link-${s.slug}`}
                  >
                    {s.label}
                  </Link>
                  {i < arr.length - 1 && <span className="text-gold-500/50">·</span>}
                </span>
              ))}
            </div>
            <div className="mt-12 flex flex-wrap gap-4">
              <Link to="/contact" className="btn-gold" data-testid="hero-cta-primary">
                {t("hero.cta_primary")} <i className="fa-solid fa-arrow-right text-[10px]" />
              </Link>
              <Link to="/services" className="btn-outline-gold" data-testid="hero-cta-secondary">
                {t("hero.cta_secondary")}
              </Link>
            </div>
          </div>

          <div className="md:col-span-5 hidden md:block animate-fade-up">
            <div className="relative">
              <div className="absolute -top-5 -left-5 w-28 h-28 border border-gold-500/70" />
              <div className="absolute -bottom-5 -right-5 w-28 h-28 border border-gold-500/70" />
              <img
                src={ASSETS.attorneySeated}
                alt="Vasileios Filippidis, Attorney-at-Law"
                className="relative w-full aspect-[3/4] object-cover object-top grayscale-[15%]"
                data-testid="hero-attorney-portrait"
              />
              <div className="relative bg-ink-700 border-l-2 border-gold-500 px-6 py-5 mt-6">
                <p className="font-serif italic text-lg text-bone-100 leading-snug">
                  “{t("hero.promise")}”
                </p>
                <div className="mt-3 uppercase tracking-[0.28em] text-[10px] text-gold-400">
                  Vasileios Filippidis · Attorney-at-Law
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="hairline-divider" />
      </section>

      {/* INTRO */}
      <section className="section py-24 md:py-32 grid md:grid-cols-12 gap-12 items-start">
        <div className="md:col-span-4">
          <div className="eyebrow mb-6">{t("home.intro.eyebrow")}</div>
        </div>
        <div className="md:col-span-8">
          <h2 className="font-serif font-light text-3xl md:text-5xl leading-[1.15] tracking-tight text-ink-800 max-w-3xl">
            {t("home.intro.title")}
          </h2>
          <p className="mt-8 text-ink-800/75 leading-[1.85] text-lg max-w-3xl">
            {t("home.intro.body")}
          </p>
        </div>
      </section>

      <div className="hairline-divider section" />

      {/* SERVICES OVERVIEW */}
      <section className="section py-24 md:py-32" data-testid="home-services">
        <div className="grid md:grid-cols-12 gap-12 items-end mb-16">
          <div className="md:col-span-7">
            <div className="eyebrow mb-6">{t("services.eyebrow")}</div>
            <h2 className="font-serif font-light text-4xl md:text-5xl leading-tight tracking-tight text-ink-800">
              {t("services.title")}
            </h2>
          </div>
          <div className="md:col-span-5">
            <p className="text-ink-800/70 leading-relaxed">{t("services.subtitle")}</p>
          </div>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-ink-800/10">
          {SERVICES.map((s, idx) => (
            <Link
              key={s.slug}
              to={`/services/${s.slug}`}
              className="group bg-bone-50 p-10 flex flex-col gap-6 transition-colors hover:bg-ink-800 hover:text-bone-50"
              data-testid={`service-card-${s.slug}`}
            >
              <div className="flex items-start justify-between">
                <i className={`${s.icon} text-3xl text-gold-600 group-hover:text-gold-400 transition-colors`} />
                <span className="font-serif text-sm text-ink-800/30 group-hover:text-bone-100/30 transition-colors">
                  0{idx + 1}
                </span>
              </div>
              <h3 className="font-serif text-2xl md:text-3xl leading-snug tracking-tight pr-4">
                {t(`service.${s.slug}.title`)}
              </h3>
              <p className="text-sm leading-relaxed opacity-75 flex-1">
                {t(`service.${s.slug}.short`)}
              </p>
              <span className="inline-flex items-center gap-2 uppercase tracking-[0.22em] text-[10px] text-gold-600 group-hover:text-gold-400 transition-colors mt-2">
                Learn more <i className="fa-solid fa-arrow-right" />
              </span>
            </Link>
          ))}
        </div>
      </section>

      {/* ABOUT PREVIEW */}
      <section className="bg-ink-800 text-bone-50 py-24 md:py-32" data-testid="home-about">
        <div className="section grid md:grid-cols-12 gap-14 items-center">
          <div className="md:col-span-5">
            <div className="relative">
              <img
                src={ASSETS.attorneyStanding}
                alt="Vasileios Filippidis"
                className="w-full object-contain aspect-[4/5]"
              />
              <div className="absolute -bottom-6 -right-6 w-32 h-32 border border-gold-500 hidden md:block" />
            </div>
          </div>
          <div className="md:col-span-7">
            <div className="eyebrow !text-gold-400 mb-6">{t("home.about.eyebrow")}</div>
            <h2 className="font-serif font-light text-4xl md:text-5xl leading-tight tracking-tight">
              {t("home.about.title")}
            </h2>
            <p className="mt-8 text-bone-100/80 leading-[1.85] text-lg max-w-xl">
              {t("home.about.body")}
            </p>
            <Link to="/about" className="btn-outline-gold mt-10" data-testid="home-about-cta">
              {t("home.about.cta")} <i className="fa-solid fa-arrow-right text-[10px]" />
            </Link>
          </div>
        </div>
      </section>

      {/* PROCESS */}
      <section className="section py-24 md:py-32" data-testid="home-process">
        <div className="grid md:grid-cols-12 gap-10 mb-16">
          <div className="md:col-span-5">
            <div className="eyebrow mb-6">{t("home.process.eyebrow")}</div>
            <h2 className="font-serif font-light text-4xl md:text-5xl leading-tight tracking-tight text-ink-800">
              {t("home.process.title")}
            </h2>
          </div>
        </div>

        <div className="grid md:grid-cols-4 gap-8">
          {[1, 2, 3, 4].map((n) => (
            <div key={n} className="border-t border-gold-500 pt-8" data-testid={`process-step-${n}`}>
              <div className="font-serif text-5xl text-gold-600 mb-6">0{n}</div>
              <h3 className="font-serif text-xl text-ink-800 mb-3">
                {t(`home.process.step${n}.title`)}
              </h3>
              <p className="text-ink-800/70 leading-relaxed text-sm">
                {t(`home.process.step${n}.body`)}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA STRIP */}
      <section className="relative overflow-hidden bg-ink-700">
        <div
          className="absolute inset-0 opacity-15"
          style={{ backgroundImage: `url(${ASSETS.library})`, backgroundSize: "cover", backgroundPosition: "center" }}
        />
        <div className="relative section py-20 md:py-28 grid md:grid-cols-12 gap-8 items-center">
          <div className="md:col-span-8 text-bone-50">
            <h2 className="font-serif font-light text-3xl md:text-5xl leading-tight tracking-tight">
              {t("home.cta.title")}
            </h2>
            <p className="mt-5 text-bone-100/80 leading-relaxed max-w-2xl">{t("home.cta.body")}</p>
          </div>
          <div className="md:col-span-4 flex md:justify-end">
            <Link to="/contact" className="btn-gold" data-testid="home-bottom-cta">
              {t("home.cta.button")} <i className="fa-solid fa-arrow-right text-[10px]" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
